import { HHStack, HHTypography } from '@hinge-health/react-component-library';
import { ResolveStatus } from '../../context/types';

const COLOR_BY_STATUS: Record<ResolveStatus, string> = {
  pending: 'rgba(0,0,0,0.6)',
  existing: '#198754',
  new: '#0d6efd',
  conflict: '#dc3545',
  error: '#fd7e14',
};

export interface DedupeCounts {
  existing: number;
  new: number;
  conflict: number;
  pending: number;
  error: number;
}

export const computeCounts = (
  statuses: Iterable<ResolveStatus>,
): DedupeCounts => {
  const counts: DedupeCounts = {
    existing: 0,
    new: 0,
    conflict: 0,
    pending: 0,
    error: 0,
  };
  for (const s of statuses) counts[s] += 1;
  return counts;
};

interface DedupeSummaryProps {
  counts: DedupeCounts;
  /** Optional message appended after the counts (e.g. "must be resolved before continuing"). */
  trailing?: string;
}

const Pill = ({
  status,
  count,
}: {
  status: ResolveStatus;
  count: number;
}): JSX.Element => (
  <HHStack
    direction="row"
    spacing={0.5}
    alignItems="baseline"
    sx={{ color: COLOR_BY_STATUS[status] }}
  >
    <HHTypography
      hhVariant="subtitle2"
      sx={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {count}
    </HHTypography>
    <HHTypography hhVariant="caption" sx={{ textTransform: 'capitalize' }}>
      {status}
    </HHTypography>
  </HHStack>
);

export const DedupeSummary = ({
  counts,
  trailing,
}: DedupeSummaryProps): JSX.Element => (
  <HHStack
    direction="row"
    spacing={3}
    alignItems="baseline"
    sx={{
      px: 2,
      py: 1,
      borderRadius: 1,
      backgroundColor: 'rgba(0,0,0,0.03)',
    }}
  >
    <Pill status="existing" count={counts.existing} />
    <Pill status="new" count={counts.new} />
    {counts.conflict > 0 && <Pill status="conflict" count={counts.conflict} />}
    {counts.error > 0 && <Pill status="error" count={counts.error} />}
    {counts.pending > 0 && <Pill status="pending" count={counts.pending} />}
    {trailing != null && (
      <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
        — {trailing}
      </HHTypography>
    )}
  </HHStack>
);
